import { useNavigate } from 'react-router-dom';
import { Bell, LogOut, Menu, Settings, User } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/theme-toggle';
import { useAuthStore } from '@/features/auth/store';
import { useUnreadCount } from '@/features/notifications/api';
import { apiLogout } from '@/features/auth/api';
import { initialsFromName } from '@/lib/format';

interface TopbarProps {
  onOpenSidebar: () => void;
}

const ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador',
  manager: 'Gestor',
  driver: 'Motorista',
  workshop: 'Oficina',
};

export function Topbar({ onOpenSidebar }: TopbarProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const unreadQuery = useUnreadCount();
  const unreadCount = unreadQuery.data?.data?.count ?? 0;

  const handleLogout = async () => {
    await apiLogout();
    clear();
    toast.success('Sessão encerrada');
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-md sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenSidebar}
        aria-label="Abrir menu"
      >
        <Menu className="size-5" />
      </Button>

      <div className="flex flex-1 items-center">
        {/* Espaço reservado para futuras busca global / breadcrumbs */}
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          onClick={() => navigate('/app/notifications')}
          aria-label="Notificações"
        >
          <Bell className="size-4" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>

        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 px-2">
              <Avatar className="size-8">
                <AvatarFallback>{initialsFromName(user?.name)}</AvatarFallback>
              </Avatar>
              <div className="hidden flex-col items-start text-left leading-tight md:flex">
                <span className="text-sm font-medium">{user?.name ?? '—'}</span>
                <span className="text-xs text-muted-foreground">
                  {user ? ROLE_LABEL[user.role] : ''}
                </span>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Minha conta</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/app/profile')}>
              <User className="size-4" />
              Perfil
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Settings className="size-4" />
              Preferências
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive">
              <LogOut className="size-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
